<div class="relative flex items-center gap-2.5 py-3.5 flex-wrap">
    <button mat-button class="toggle-notes-btn" (click)="showErrorNotes = !showErrorNotes">{{ showErrorNotes ? "✕ Hide Error Guide" : "⚠️ Error Guide" }}</button>
    <div class="error-notes overlay" *ngIf="showErrorNotes">
      <p class="notes-title">⚠️ Error Indicators</p>
      <ul class="notes-list">
        <li><b>Eksteen (H⁺):</b> Eksteen value must be present when pH KCl &lt; 5.5</li>
        <li><b>Ohms:</b> When Na &gt; 50, consider Ohms analysis</li>
        <li><b>Texture Code:</b> Texture code is questioned when T-value does not match the expected range
          <table class="texture-table" *ngIf="textureValues.length">
            <tr><th>Code</th><th>Description</th><th>T-Value Range</th></tr>
            <tr *ngFor="let t of textureValues"><td>{{t.code}}</td><td>{{t.description}}</td><td>{{t.minValue == 0 ? "&lt; " + t.maxValue : t.maxValue == 999 ? "> " + t.minValue : t.minValue + " - " + t.maxValue}}</td></tr>
          </table>
        </li>
        <li><b>P Bray 1:</b> When pH &gt; 6.5 — discuss Olsen analysis with client</li>
      </ul>
    </div>
    <div class="flex-1"></div>
    <!-- Search -->
    <div class="relative">
        <span class="material-icons absolute left-2.5 top-1/2 -translate-y-1/2 text-[18px] text-[var(--tertiary-text)]">search</span>
        <input type="text" placeholder="Search samples..." [(ngModel)]="searchText"
            class="py-2 pl-9 pr-3 border-[1.5px] border-[var(--app-border)] rounded-lg text-[13px] w-[200px] outline-none transition-colors bg-[var(--app-base)] focus:border-[var(--primary-dark)] text-[var(--base-text-alt-dark)]">
    </div>
</div>

<div class="w-full overflow-auto mt-4 mb-2.5 max-h-[calc(100vh-520px)]">
    <div class="min-w-[6500px] mr-2.5">
        <!-- Every cell carries the same alignment and horizontal padding as its own header:
             px-3.5 and left for the sample columns, px-2 and centred for the analysis ones.
             Without classes a cell falls back to Material's own padding, which does not match
             either header, so the values sat off to the right of the column they belong to.
             The soft tissue table next door is the reference. -->
        <table mat-table [dataSource]="sampleSource.data">

            <ng-container matColumnDef="farm">
                <th mat-header-cell *matHeaderCellDef class="!py-3 !px-3.5 !text-[11px] !font-bold !uppercase !tracking-wider !text-[var(--tertiary-text)] !bg-[var(--app-base)] !text-left !whitespace-nowrap !border-b !border-[var(--app-base)]"> Farm </th>
                <td mat-cell *matCellDef="let element" class="!py-2 !px-3.5 !text-[13px] !font-medium !text-[var(--base-text-alt-dark)] !border-b !border-[var(--app-base)]"> {{element?.farmName ?? '-'}} </td>
            </ng-container>

            <ng-container matColumnDef="field">
                <th mat-header-cell class="!py-3 !px-3.5 !text-[11px] !font-bold !uppercase !tracking-wider !text-[var(--tertiary-text)] !bg-[var(--app-base)] !text-left !whitespace-nowrap !border-b !border-[var(--app-base)]" *matHeaderCellDef> Field </th>
                <td mat-cell *matCellDef="let element" class="!py-2 !px-3.5 !text-[13px] !font-medium !text-[var(--base-text-alt-dark)] !border-b !border-[var(--app-base)]"> {{element?.fieldName ?? '-'}} </td>
            </ng-container>

            <ng-container matColumnDef="sample">
                <th mat-header-cell class="!py-3 !px-3.5 !text-[11px] !font-bold !uppercase !tracking-wider !text-[var(--tertiary-text)] !bg-[var(--app-base)] !text-left !whitespace-nowrap !border-b !border-[var(--app-base)]" *matHeaderCellDef> Sample </th>
                <td mat-cell *matCellDef="let element" class="!py-2 !px-3.5 !text-[13px] !font-medium !text-[var(--base-text-alt-dark)] !border-b !border-[var(--app-base)]"> {{element?.sampleName ?? '-'}} </td>
            </ng-container>

            <ng-container matColumnDef="sampleReference">
                <th mat-header-cell class="!py-3 !px-3.5 !text-[11px] !font-bold !uppercase !tracking-wider !text-[var(--tertiary-text)] !bg-[var(--app-base)] !text-left !whitespace-nowrap !border-b !border-[var(--app-base)]" *matHeaderCellDef> Sample Reference </th>
                <td mat-cell *matCellDef="let element" class="!py-2 !px-3.5 !text-[13px] !font-normal !text-[var(--tertiary-text)] !border-b !border-[var(--app-base)]"> {{element?.sampleNum ?? '-'}} </td>
            </ng-container>

            <ng-container matColumnDef="rockPercentage">
                <th mat-header-cell class="!py-3 !px-3.5 !text-[11px] !font-bold !uppercase !tracking-wider !text-[var(--tertiary-text)] !bg-[var(--app-base)] !text-left !whitespace-nowrap !border-b !border-[var(--app-base)]" *matHeaderCellDef> Rock % </th>
                <td mat-cell *matCellDef="let element" class="!py-2 !px-3.5 !text-[13px] !font-normal !text-[var(--tertiary-text)] !border-b !border-[var(--app-base)]"> {{element?.rockPercentage ?? '-'}} </td>
            </ng-container>

            <ng-container matColumnDef="sampleDepth">
                <th mat-header-cell *matHeaderCellDef class="!py-3 !px-3.5 !text-[11px] !font-bold !uppercase !tracking-wider !text-[var(--tertiary-text)] !bg-[var(--app-base)] !text-left !whitespace-nowrap !border-b !border-[var(--app-base)]"> Sample Depth </th>
                <td mat-cell *matCellDef="let element" class="!py-2 !px-3.5 !text-[13px] !font-normal !text-[var(--tertiary-text)] !border-b !border-[var(--app-base)]"> {{element?.sampleDepth ?? '-'}} </td>
            </ng-container>

            <ng-container matColumnDef="blank">
                <th mat-header-cell *matHeaderCellDef class="!w-[10px] !p-0 !bg-white !border-none"></th>
                <td mat-cell *matCellDef="let element" class="!w-[10px] !p-0 !border-none"></td>
            </ng-container>

            <!-- Batch & Texture -->

            <ng-container matColumnDef="batchNum">
                <th mat-header-cell *matHeaderCellDef class="!py-3 !px-2 !text-[11px] !font-bold !text-white !bg-[var(--primary-dark)] !text-center !whitespace-nowrap !border-b !border-[var(--primary-dark)]">
                    <div class="div-col">
                        <span style="font-weight: 700">Batch No.</span>
                    </div>
                </th>
                <td mat-cell *matCellDef="let element" class="!py-2 !px-2 !text-[13px] !text-center !border-b !border-[var(--app-base)]">
                    <div class="analysis-element" [ngClass]="{'has-error': element?.batchNum?.hasError}">
                        {{element?.batchNum?.value ?? '-'}}
                    </div>
                </td>
            </ng-container>

            <ng-container matColumnDef="textureClay">
                <th mat-header-cell class="!py-3 !px-2 !text-[11px] !font-bold !text-white !bg-[var(--primary-dark)] !text-center !whitespace-nowrap !border-b !border-[var(--primary-dark)]" *matHeaderCellDef>
                    <div class="div-col">
                        <span style="font-weight: 700">Texture Clay</span>
                    </div>
                </th>
                <td mat-cell *matCellDef="let element" class="!py-2 !px-2 !text-[13px] !text-center !border-b !border-[var(--app-base)]">
                    <div class="analysis-element" [ngClass]="{'has-error': element?.textureClay?.hasError}">
                        {{element?.textureClay?.value ?? '-'}}
                    </div>
                </td>
            </ng-container>

            <ng-container matColumnDef="textureSilt">
                <th mat-header-cell class="!py-3 !px-2 !text-[11px] !font-bold !text-white !bg-[var(--primary-dark)] !text-center !whitespace-nowrap !border-b !border-[var(--primary-dark)]" *matHeaderCellDef>
                    <div class="div-col">
                        <span style="font-weight: 700">Texture Silt</span>
                    </div>
                </th>
                <td mat-cell *matCellDef="let element" class="!py-2 !px-2 !text-[13px] !text-center !border-b !border-[var(--app-base)]">
                    <div class="analysis-element" [ngClass]="{'has-error': element?.textureSilt?.hasError}">
                        {{element?.textureSilt?.value ?? '-'}}
                    </div>
                </td>
            </ng-container>

            <ng-container matColumnDef="textureSand">
                <th mat-header-cell class="!py-3 !px-2 !text-[11px] !font-bold !text-white !bg-[var(--primary-dark)] !text-center !whitespace-nowrap !border-b !border-[var(--primary-dark)]" *matHeaderCellDef>
                    <div class="div-col">
                        <span style="font-weight: 700">Texture Sand</span>
                    </div>
                </th>
                <td mat-cell *matCellDef="let element" class="!py-2 !px-2 !text-[13px] !text-center !border-b !border-[var(--app-base)]">
                    <div class="analysis-element" [ngClass]="{'has-error': element?.textureSand?.hasError}">
                        {{element?.textureSand?.value ?? '-'}}
                    </div>
                </td>
            </ng-container>

            <ng-container matColumnDef="textureCode">
                <th mat-header-cell class="!py-3 !px-2 !text-[11px] !font-bold !text-white !bg-[var(--primary-dark)] !text-center !whitespace-nowrap !border-b !border-[var(--primary-dark)]" *matHeaderCellDef>
                    <div class="div-col">
                        <span style="font-weight: 700">Texture Code</span>
                    </div>
                </th>
                <td mat-cell *matCellDef="let element" class="!py-2 !px-2 !text-[13px] !text-center !border-b !border-[var(--app-base)]">
                    <div class="analysis-element" [ngClass]="{'has-error': element?.textureCode?.hasError}">
                        {{element?.textureCode?.value ?? '-'}}
                    </div>
                </td>
            </ng-container>

            <!-- Eksteen & pH -->

            <ng-container matColumnDef="eksteen">
                <th mat-header-cell class="!py-3 !px-2 !text-[11px] !font-bold !text-white !bg-[var(--primary-dark)] !text-center !whitespace-nowrap !border-b !border-[var(--primary-dark)]" *matHeaderCellDef>
                    <div class="div-col">
                        <span style="font-weight: 700">Eksteen</span>
                    </div>
                </th>
                <td mat-cell *matCellDef="let element" class="!py-2 !px-2 !text-[13px] !text-center !border-b !border-[var(--app-base)]">
                    <div class="analysis-element" [ngClass]="{'has-error': element?.eksteen?.hasError}">
                        {{element?.eksteen?.value ?? '-'}}
                    </div>
                    @if(element?.eksteen?.value) {
                        <div class="analysis-element" [ngClass]="{'has-error': element?.estimatedEksteen?.hasError}">
                            Estimated: {{element?.estimatedEksteen?.value ? (element?.estimatedEksteen?.value | number: '0.2-2') : '-'}}
                        </div>
                    }
                </td>
            </ng-container>

            <!-- <ng-container matColumnDef="estimatedEksteen">
                <th mat-header-cell class="!py-3 !px-2 !text-[11px] !font-bold !text-white !bg-[var(--primary-dark)] !text-center !whitespace-nowrap !border-b !border-[var(--primary-dark)]" *matHeaderCellDef>
                    <div class="div-col">
                        <span style="font-weight: 700">Est. Eksteen</span>
                    </div>
                </th>
                <td mat-cell *matCellDef="let element" class="!py-2 !px-2 !text-[13px] !text-center !border-b !border-[var(--app-base)]">
                    <div class="analysis-element" [ngClass]="{'has-error': element?.estimatedEksteen?.hasError}">
                        {{element?.estimatedEksteen?.value ?? '-'}}
                    </div>
                </td>
            </ng-container> -->

            <ng-container matColumnDef="phKcl">
                <th mat-header-cell class="!py-3 !px-2 !text-[11px] !font-bold !text-white !bg-[var(--primary-dark)] !text-center !whitespace-nowrap !border-b !border-[var(--primary-dark)]" *matHeaderCellDef>
                    <div class="div-col">
                        <span style="font-weight: 700">pHKCl</span>
                    </div>
                </th>
                <td mat-cell *matCellDef="let element" class="!py-2 !px-2 !text-[13px] !text-center !border-b !border-[var(--app-base)]">
                    <div class="analysis-element" [ngClass]="{'has-error': element?.phKcl?.hasError}">
                        {{element?.phKcl?.value ?? '-'}}
                    </div>
                </td>
            </ng-container>

            <!-- Major Elements (%) -->

            <ng-container matColumnDef="cl">
                <th mat-header-cell class="!py-3 !px-2 !text-[11px] !font-bold !text-white !bg-[var(--primary-dark)] !text-center !whitespace-nowrap !border-b !border-[var(--primary-dark)]" *matHeaderCellDef>
                    <div class="div-col">
                        <span style="font-weight: 700">Cl</span>
                        <span style="text-transform: lowercase">mg/kg</span>
                    </div>
                </th>
                <td mat-cell *matCellDef="let element" class="!py-2 !px-2 !text-[13px] !text-center !border-b !border-[var(--app-base)]">
                    <div class="analysis-element" [ngClass]="{'has-error': element?.cl?.hasError}">
                        {{element?.cl?.value ?? '-'}}
                    </div>
                </td>
            </ng-container>

            <ng-container matColumnDef="potassium">
                <th mat-header-cell class="!py-3 !px-2 !text-[11px] !font-bold !text-white !bg-[var(--primary-dark)] !text-center !whitespace-nowrap !border-b !border-[var(--primary-dark)]" *matHeaderCellDef>
                    <div class="div-col">
                        <span style="font-weight: 700">K</span>
                        <span style="text-transform: lowercase">mg/kg</span>
                    </div>
                </th>
                <td mat-cell *matCellDef="let element" class="!py-2 !px-2 !text-[13px] !text-center !border-b !border-[var(--app-base)]">
                    <div class="analysis-element" [ngClass]="{'has-error': element?.potassium?.hasError}">
                        {{element?.potassium?.value ?? '-'}}
                    </div>
                </td>
            </ng-container>

            <ng-container matColumnDef="calcium">
                <th mat-header-cell class="!py-3 !px-2 !text-[11px] !font-bold !text-white !bg-[var(--primary-dark)] !text-center !whitespace-nowrap !border-b !border-[var(--primary-dark)]" *matHeaderCellDef>
                    <div class="div-col">
                        <span style="font-weight: 700">Ca</span>
                        <span style="text-transform: lowercase">mg/kg</span>
                    </div>
                </th>
                <td mat-cell *matCellDef="let element" class="!py-2 !px-2 !text-[13px] !text-center !border-b !border-[var(--app-base)]">
                    <div class="analysis-element" [ngClass]="{'has-error': element?.calcium?.hasError}">
                        {{element?.calcium?.value ?? '-'}}
                    </div>
                </td>
            </ng-container>

            <ng-container matColumnDef="magnesium">
                <th mat-header-cell class="!py-3 !px-2 !text-[11px] !font-bold !text-white !bg-[var(--primary-dark)] !text-center !whitespace-nowrap !border-b !border-[var(--primary-dark)]" *matHeaderCellDef>
                    <div class="div-col">
                        <span style="font-weight: 700">Mg</span>
                        <span style="text-transform: lowercase">mg/kg</span>
                    </div>
                </th>
                <td mat-cell *matCellDef="let element" class="!py-2 !px-2 !text-[13px] !text-center !border-b !border-[var(--app-base)]">
                    <div class="analysis-element" [ngClass]="{'has-error': element?.magnesium?.hasError}">
                        {{element?.magnesium?.value ?? '-'}}
                    </div>
                </td>
            </ng-container>

            <ng-container matColumnDef="sodium">
                <th mat-header-cell class="!py-3 !px-2 !text-[11px] !font-bold !text-white !bg-[var(--primary-dark)] !text-center !whitespace-nowrap !border-b !border-[var(--primary-dark)]" *matHeaderCellDef>
                    <div class="div-col">
                        <span style="font-weight: 700">Na</span>
                        <span style="text-transform: lowercase">mg/kg</span>
                    </div>
                </th>
                <td mat-cell *matCellDef="let element" class="!py-2 !px-2 !text-[13px] !text-center !border-b !border-[var(--app-base)]">
                    <div class="analysis-element" [ngClass]="{'has-error': element?.sodium?.hasError}">
                        {{element?.sodium?.value ?? '-'}}
                    </div>
                </td>
            </ng-container>

            <!-- Conductivity & Organic -->

            <ng-container matColumnDef="ohms">
                <th mat-header-cell class="!py-3 !px-2 !text-[11px] !font-bold !text-white !bg-[var(--primary-dark)] !text-center !whitespace-nowrap !border-b !border-[var(--primary-dark)]" *matHeaderCellDef>
                    <div class="div-col">
                        <span style="font-weight: 700">Ohms</span>
                    </div>
                </th>
                <td mat-cell *matCellDef="let element" class="!py-2 !px-2 !text-[13px] !text-center !border-b !border-[var(--app-base)]">
                    <div class="analysis-element" [ngClass]="{'has-error': element?.ohms?.hasError}">
                        {{element?.ohms?.value != null ? (element.ohms.value | number:'1.0-2') : '-'}}
                    </div>
                </td>
            </ng-container>

            <ng-container matColumnDef="carbon">
                <th mat-header-cell class="!py-3 !px-2 !text-[11px] !font-bold !text-white !bg-[var(--primary-dark)] !text-center !whitespace-nowrap !border-b !border-[var(--primary-dark)]" *matHeaderCellDef>
                    <div class="div-col">
                        <span style="font-weight: 700">Carbon</span>
                        <span style="text-transform: lowercase">mg/kg</span>
                    </div>
                </th>
                <td mat-cell *matCellDef="let element" class="!py-2 !px-2 !text-[13px] !text-center !border-b !border-[var(--app-base)]">
                    <div class="analysis-element" [ngClass]="{'has-error': element?.carbon?.hasError}">
                        {{element?.carbon?.value ?? '-'}}
                    </div>
                </td>
            </ng-container>

            <ng-container matColumnDef="sulphur">
                <th mat-header-cell class="!py-3 !px-2 !text-[11px] !font-bold !text-white !bg-[var(--primary-dark)] !text-center !whitespace-nowrap !border-b !border-[var(--primary-dark)]" *matHeaderCellDef>
                    <div class="div-col">
                        <span style="font-weight: 700">S</span>
                        <span style="text-transform: lowercase">mg/kg</span>
                    </div>
                </th>
                <td mat-cell *matCellDef="let element" class="!py-2 !px-2 !text-[13px] !text-center !border-b !border-[var(--app-base)]">
                    <div class="analysis-element" [ngClass]="{'has-error': element?.sulphur?.hasError}">
                        {{element?.sulphur?.value ?? '-'}}
                    </div>
                </td>
            </ng-container>

            <!-- Trace Elements (mg/kg) -->

            <ng-container matColumnDef="traceCu">
                <th mat-header-cell class="!py-3 !px-2 !text-[11px] !font-bold !text-white !bg-[var(--primary-dark)] !text-center !whitespace-nowrap !border-b !border-[var(--primary-dark)]" *matHeaderCellDef>
                    <div class="div-col">
                        <span style="font-weight: 700">Trace Cu</span>
                        <span style="text-transform: lowercase">mg/kg</span>
                    </div>
                </th>
                <td mat-cell *matCellDef="let element" class="!py-2 !px-2 !text-[13px] !text-center !border-b !border-[var(--app-base)]">
                    <div class="analysis-element" [ngClass]="{'has-error': element?.traceCu?.hasError}">
                        {{element?.traceCu?.value ?? '-'}}
                    </div>
                </td>
            </ng-container>

            <ng-container matColumnDef="traceZn">
                <th mat-header-cell class="!py-3 !px-2 !text-[11px] !font-bold !text-white !bg-[var(--primary-dark)] !text-center !whitespace-nowrap !border-b !border-[var(--primary-dark)]" *matHeaderCellDef>
                    <div class="div-col">
                        <span style="font-weight: 700">Trace Zn</span>
                        <span style="text-transform: lowercase">mg/kg</span>
                    </div>
                </th>
                <td mat-cell *matCellDef="let element" class="!py-2 !px-2 !text-[13px] !text-center !border-b !border-[var(--app-base)]">
                    <div class="analysis-element" [ngClass]="{'has-error': element?.traceZn?.hasError}">
                        {{element?.traceZn?.value ?? '-'}}
                    </div>
                </td>
            </ng-container>

            <ng-container matColumnDef="traceMn">
                <th mat-header-cell class="!py-3 !px-2 !text-[11px] !font-bold !text-white !bg-[var(--primary-dark)] !text-center !whitespace-nowrap !border-b !border-[var(--primary-dark)]" *matHeaderCellDef>
                    <div class="div-col">
                        <span style="font-weight: 700">Trace Mn</span>
                        <span style="text-transform: lowercase">mg/kg</span>
                    </div>
                </th>
                <td mat-cell *matCellDef="let element" class="!py-2 !px-2 !text-[13px] !text-center !border-b !border-[var(--app-base)]">
                    <div class="analysis-element" [ngClass]="{'has-error': element?.traceMn?.hasError}">
                        {{element?.traceMn?.value ?? '-'}}
                    </div>
                </td>
            </ng-container>

            <ng-container matColumnDef="traceB">
                <th mat-header-cell class="!py-3 !px-2 !text-[11px] !font-bold !text-white !bg-[var(--primary-dark)] !text-center !whitespace-nowrap !border-b !border-[var(--primary-dark)]" *matHeaderCellDef>
                    <div class="div-col">
                        <span style="font-weight: 700">Trace B</span>
                        <span style="text-transform: lowercase">mg/kg</span>
                    </div>
                </th>
                <td mat-cell *matCellDef="let element" class="!py-2 !px-2 !text-[13px] !text-center !border-b !border-[var(--app-base)]">
                    <div class="analysis-element" [ngClass]="{'has-error': element?.traceB?.hasError}">
                        {{element?.traceB?.value ?? '-'}}
                    </div>
                </td>
            </ng-container>

            <ng-container matColumnDef="traceFe">
                <th mat-header-cell class="!py-3 !px-2 !text-[11px] !font-bold !text-white !bg-[var(--primary-dark)] !text-center !whitespace-nowrap !border-b !border-[var(--primary-dark)]" *matHeaderCellDef>
                    <div class="div-col">
                        <span style="font-weight: 700">Trace Fe</span>
                        <span style="text-transform: lowercase">mg/kg</span>
                    </div>
                </th>
                <td mat-cell *matCellDef="let element" class="!py-2 !px-2 !text-[13px] !text-center !border-b !border-[var(--app-base)]">
                    <div class="analysis-element" [ngClass]="{'has-error': element?.traceFe?.hasError}">
                        {{element?.traceFe?.value ?? '-'}}
                    </div>
                </td>
            </ng-container>

            <!-- Anions & Nitrogen -->

            <ng-container matColumnDef="nh4">
                <th mat-header-cell class="!py-3 !px-2 !text-[11px] !font-bold !text-white !bg-[var(--primary-dark)] !text-center !whitespace-nowrap !border-b !border-[var(--primary-dark)]" *matHeaderCellDef>
                    <div class="div-col">
                        <span style="font-weight: 700">NH4</span>
                    </div>
                </th>
                <td mat-cell *matCellDef="let element" class="!py-2 !px-2 !text-[13px] !text-center !border-b !border-[var(--app-base)]">
                    <div class="analysis-element" [ngClass]="{'has-error': element?.nh4?.hasError}">
                        {{element?.nh4?.value ?? '-'}}
                    </div>
                </td>
            </ng-container>

            <ng-container matColumnDef="no3">
                <th mat-header-cell class="!py-3 !px-2 !text-[11px] !font-bold !text-white !bg-[var(--primary-dark)] !text-center !whitespace-nowrap !border-b !border-[var(--primary-dark)]" *matHeaderCellDef>
                    <div class="div-col">
                        <span style="font-weight: 700">NO3</span>
                    </div>
                </th>
                <td mat-cell *matCellDef="let element" class="!py-2 !px-2 !text-[13px] !text-center !border-b !border-[var(--app-base)]">
                    <div class="analysis-element" [ngClass]="{'has-error': element?.no3?.hasError}">
                        {{element?.no3?.value ?? '-'}}
                    </div>
                </td>
            </ng-container>

            <!-- Physical Properties -->

            <ng-container matColumnDef="density">
                <th mat-header-cell class="!py-3 !px-2 !text-[11px] !font-bold !text-white !bg-[var(--primary-dark)] !text-center !whitespace-nowrap !border-b !border-[var(--primary-dark)]" *matHeaderCellDef>
                    <div class="div-col">
                        <span style="font-weight: 700">Density</span>
                    </div>
                </th>
                <td mat-cell *matCellDef="let element" class="!py-2 !px-2 !text-[13px] !text-center !border-b !border-[var(--app-base)]">
                    <div class="analysis-element" [ngClass]="{'has-error': element?.density?.hasError}">
                        {{element?.density?.value ?? '-'}}
                    </div>
                </td>
            </ng-container>

            <ng-container matColumnDef="gf">
                <th mat-header-cell class="!py-3 !px-2 !text-[11px] !font-bold !text-white !bg-[var(--primary-dark)] !text-center !whitespace-nowrap !border-b !border-[var(--primary-dark)]" *matHeaderCellDef>
                    <div class="div-col">
                        <span style="font-weight: 700">GF</span>
                    </div>
                </th>
                <td mat-cell *matCellDef="let element" class="!py-2 !px-2 !text-[13px] !text-center !border-b !border-[var(--app-base)]">
                    <div class="analysis-element" [ngClass]="{'has-error': element?.gf?.hasError}">
                        {{element?.gf?.value ?? '-'}}
                    </div>
                </td>
            </ng-container>

            <!-- Cation Exchange (me%) -->

            <ng-container matColumnDef="kme">
                <th mat-header-cell class="!py-3 !px-2 !text-[11px] !font-bold !text-white !bg-[var(--primary-dark)] !text-center !whitespace-nowrap !border-b !border-[var(--primary-dark)]" *matHeaderCellDef>
                    <div class="div-col">
                        <span style="font-weight: 700">K</span>
                        <span style="text-transform: lowercase">me%</span>
                    </div>
                </th>
                <td mat-cell *matCellDef="let element" class="!py-2 !px-2 !text-[13px] !text-center !border-b !border-[var(--app-base)]">
                    <div class="analysis-element" [ngClass]="{'has-error': element?.kme?.hasError}">
                        {{element?.kme?.value ? (element?.kme?.value | number: '0.2-2') : '-'}}
                    </div>
                </td>
            </ng-container>

            <ng-container matColumnDef="caMe">
                <th mat-header-cell class="!py-3 !px-2 !text-[11px] !font-bold !text-white !bg-[var(--primary-dark)] !text-center !whitespace-nowrap !border-b !border-[var(--primary-dark)]" *matHeaderCellDef>
                    <div class="div-col">
                        <span style="font-weight: 700">Ca</span>
                        <span style="text-transform: lowercase">me%</span>
                    </div>
                </th>
                <td mat-cell *matCellDef="let element" class="!py-2 !px-2 !text-[13px] !text-center !border-b !border-[var(--app-base)]">
                    <div class="analysis-element" [ngClass]="{'has-error': element?.caMe?.hasError}">
                        {{element?.caMe?.value ? (element?.caMe?.value | number: '0.2-2') : '-'}}
                    </div>
                </td>
            </ng-container>

            <ng-container matColumnDef="mgMe">
                <th mat-header-cell class="!py-3 !px-2 !text-[11px] !font-bold !text-white !bg-[var(--primary-dark)] !text-center !whitespace-nowrap !border-b !border-[var(--primary-dark)]" *matHeaderCellDef>
                    <div class="div-col">
                        <span style="font-weight: 700">Mg</span>
                        <span style="text-transform: lowercase">me%</span>
                    </div>
                </th>
                <td mat-cell *matCellDef="let element" class="!py-2 !px-2 !text-[13px] !text-center !border-b !border-[var(--app-base)]">
                    <div class="analysis-element" [ngClass]="{'has-error': element?.mgMe?.hasError}">
                        {{element?.mgMe?.value ? (element?.mgMe?.value | number: '0.2-2') : '-'}}
                    </div>
                </td>
            </ng-container>

            <ng-container matColumnDef="naMe">
                <th mat-header-cell class="!py-3 !px-2 !text-[11px] !font-bold !text-white !bg-[var(--primary-dark)] !text-center !whitespace-nowrap !border-b !border-[var(--primary-dark)]" *matHeaderCellDef>
                    <div class="div-col">
                        <span style="font-weight: 700">Na</span>
                        <span style="text-transform: lowercase">me%</span>
                    </div>
                </th>
                <td mat-cell *matCellDef="let element" class="!py-2 !px-2 !text-[13px] !text-center !border-b !border-[var(--app-base)]">
                    <div class="analysis-element" [ngClass]="{'has-error': element?.naMe?.hasError}">
                        {{element?.naMe?.value ? (element?.naMe?.value | number: '0.2-2') : '-'}}
                    </div>
                </td>
            </ng-container>

            <!-- CEC Values -->

            <ng-container matColumnDef="svalue">
                <th mat-header-cell class="!py-3 !px-2 !text-[11px] !font-bold !text-white !bg-[var(--primary-dark)] !text-center !whitespace-nowrap !border-b !border-[var(--primary-dark)]" *matHeaderCellDef>
                    <div class="div-col">
                        <span style="font-weight: 700">S-Value</span>
                    </div>
                </th>
                <td mat-cell *matCellDef="let element" class="!py-2 !px-2 !text-[13px] !text-center !border-b !border-[var(--app-base)]">
                    <div class="analysis-element" [ngClass]="{'has-error': element?.svalue?.hasError}">
                        {{element?.svalue?.value ? (element?.svalue?.value | number: '0.2-2') : '-'}}
                    </div>
                </td>
            </ng-container>

            <ng-container matColumnDef="tvalue">
                <th mat-header-cell class="!py-3 !px-2 !text-[11px] !font-bold !text-white !bg-[var(--primary-dark)] !text-center !whitespace-nowrap !border-b !border-[var(--primary-dark)]" *matHeaderCellDef>
                    <div class="div-col">
                        <span style="font-weight: 700">T-Value</span>
                    </div>
                </th>
                <td mat-cell *matCellDef="let element" class="!py-2 !px-2 !text-[13px] !text-center !border-b !border-[var(--app-base)]">
                    <div class="analysis-element" [ngClass]="{'has-error': element?.tvalue?.hasError}">
                        {{element?.tvalue?.value ? (element?.tvalue?.value | number: '0.2-2') : '-'}}
                    </div>
                </td>
            </ng-container>

            <!-- Saturation (%) -->

            <ng-container matColumnDef="bvPercentage">
                <th mat-header-cell class="!py-3 !px-2 !text-[11px] !font-bold !text-white !bg-[var(--primary-dark)] !text-center !whitespace-nowrap !border-b !border-[var(--primary-dark)]" *matHeaderCellDef>
                    <div class="div-col">
                        <span style="font-weight: 700">BV</span>
                        <span style="text-transform: lowercase">mg/kg</span>
                    </div>
                </th>
                <td mat-cell *matCellDef="let element" class="!py-2 !px-2 !text-[13px] !text-center !border-b !border-[var(--app-base)]">
                    <div class="analysis-element" [ngClass]="{'has-error': element?.bvPercentage?.hasError}">
                        {{element?.bvPercentage?.value ? (element?.bvPercentage?.value | number: '0.2-2') : '-'}}
                    </div>
                </td>
            </ng-container>

            <ng-container matColumnDef="caSatPercentage">
                <th mat-header-cell class="!py-3 !px-2 !text-[11px] !font-bold !text-white !bg-[var(--primary-dark)] !text-center !whitespace-nowrap !border-b !border-[var(--primary-dark)]" *matHeaderCellDef>
                    <div class="div-col">
                        <span style="font-weight: 700">Ca Sat</span>
                        <span style="text-transform: lowercase">mg/kg</span>
                    </div>
                </th>
                <td mat-cell *matCellDef="let element" class="!py-2 !px-2 !text-[13px] !text-center !border-b !border-[var(--app-base)]">
                    <div class="analysis-element" [ngClass]="{'has-error': element?.caSatPercentage?.hasError}">
                        {{element?.caSatPercentage?.value ? (element?.caSatPercentage?.value | number: '0.2-2') : '-'}}
                    </div>
                </td>
            </ng-container>

            <ng-container matColumnDef="mgSatPercentage">
                <th mat-header-cell class="!py-3 !px-2 !text-[11px] !font-bold !text-white !bg-[var(--primary-dark)] !text-center !whitespace-nowrap !border-b !border-[var(--primary-dark)]" *matHeaderCellDef>
                    <div class="div-col">
                        <span style="font-weight: 700">Mg Sat</span>
                        <span style="text-transform: lowercase">mg/kg</span>
                    </div>
                </th>
                <td mat-cell *matCellDef="let element" class="!py-2 !px-2 !text-[13px] !text-center !border-b !border-[var(--app-base)]">
                    <div class="analysis-element" [ngClass]="{'has-error': element?.mgSatPercentage?.hasError}">
                        {{element?.mgSatPercentage?.value ? (element?.mgSatPercentage?.value | number: '0.2-2') : '-'}}
                    </div>
                </td>
            </ng-container>

            <ng-container matColumnDef="naSatPercentage">
                <th mat-header-cell class="!py-3 !px-2 !text-[11px] !font-bold !text-white !bg-[var(--primary-dark)] !text-center !whitespace-nowrap !border-b !border-[var(--primary-dark)]" *matHeaderCellDef>
                    <div class="div-col">
                        <span style="font-weight: 700">Na Sat</span>
                        <span style="text-transform: lowercase">mg/kg</span>
                    </div>
                </th>
                <td mat-cell *matCellDef="let element" class="!py-2 !px-2 !text-[13px] !text-center !border-b !border-[var(--app-base)]">
                    <div class="analysis-element" [ngClass]="{'has-error': element?.naSatPercentage?.hasError}">
                        {{element?.naSatPercentage?.value ? (element?.naSatPercentage?.value | number: '0.2-2') : '-'}}
                    </div>
                </td>
            </ng-container>

            <ng-container matColumnDef="ksatPercentage">
                <th mat-header-cell class="!py-3 !px-2 !text-[11px] !font-bold !text-white !bg-[var(--primary-dark)] !text-center !whitespace-nowrap !border-b !border-[var(--primary-dark)]" *matHeaderCellDef>
                    <div class="div-col">
                        <span style="font-weight: 700">K Sat</span>
                        <span style="text-transform: lowercase">mg/kg</span>
                    </div>
                </th>
                <td mat-cell *matCellDef="let element" class="!py-2 !px-2 !text-[13px] !text-center !border-b !border-[var(--app-base)]">
                    <div class="analysis-element" [ngClass]="{'has-error': element?.ksatPercentage?.hasError}">
                        {{element?.ksatPercentage?.value ? (element?.ksatPercentage?.value | number: '0.2-2') : '-'}}
                    </div>
                </td>
            </ng-container>

            <!-- Lime & Phosphorus -->

            <ng-container matColumnDef="cedara">
                <th mat-header-cell class="!py-3 !px-2 !text-[11px] !font-bold !text-white !bg-[var(--primary-dark)] !text-center !whitespace-nowrap !border-b !border-[var(--primary-dark)]" *matHeaderCellDef>
                    <div class="div-col">
                        <span style="font-weight: 700">Cedara</span>
                    </div>
                </th>
                <td mat-cell *matCellDef="let element" class="!py-2 !px-2 !text-[13px] !text-center !border-b !border-[var(--app-base)]">
                    <div class="analysis-element" [ngClass]="{'has-error': element?.cedara?.hasError}">
                        {{element?.cedara?.value ? (element?.cedara?.value | number: '0.2-2') : '-'}}
                    </div>
                </td>
            </ng-container>

            <ng-container matColumnDef="vrykalk">
                <th mat-header-cell class="!py-3 !px-2 !text-[11px] !font-bold !text-white !bg-[var(--primary-dark)] !text-center !whitespace-nowrap !border-b !border-[var(--primary-dark)]" *matHeaderCellDef>
                    <div class="div-col">
                        <span style="font-weight: 700">Vrykalk</span>
                    </div>
                </th>
                <td mat-cell *matCellDef="let element" class="!py-2 !px-2 !text-[13px] !text-center !border-b !border-[var(--app-base)]">
                    <div class="analysis-element" [ngClass]="{'has-error': element?.vrykalk?.hasError}">
                        {{element?.vrykalk?.value ? (element?.vrykalk?.value | number: '0.2-2') : '-'}}
                    </div>
                </td>
            </ng-container>

            <ng-container matColumnDef="pbray1">
                <th mat-header-cell class="!py-3 !px-2 !text-[11px] !font-bold !text-white !bg-[var(--primary-dark)] !text-center !whitespace-nowrap !border-b !border-[var(--primary-dark)]" *matHeaderCellDef>
                    <div class="div-col">
                        <span style="font-weight: 700">P Bray 1</span>
                    </div>
                </th>
                <td mat-cell *matCellDef="let element" class="!py-2 !px-2 !text-[13px] !text-center !border-b !border-[var(--app-base)]">
                    <div class="analysis-element" [ngClass]="{'has-error': element?.pbray1?.hasError}">
                        {{element?.pbray1?.value ? (element?.pbray1?.value | number: '0.2-2') : '-'}}
                    </div>
                </td>
            </ng-container>

            <ng-container matColumnDef="pbray2">
                <th mat-header-cell class="!py-3 !px-2 !text-[11px] !font-bold !text-white !bg-[var(--primary-dark)] !text-center !whitespace-nowrap !border-b !border-[var(--primary-dark)]" *matHeaderCellDef>
                    <div class="div-col">
                        <span style="font-weight: 700">P Bray 2</span>
                    </div>
                </th>
                <td mat-cell *matCellDef="let element" class="!py-2 !px-2 !text-[13px] !text-center !border-b !border-[var(--app-base)]">
                    <div class="analysis-element" [ngClass]="{'has-error': element?.pbray2?.hasError}">
                        {{element?.pbray2?.value ? (element?.pbray2?.value | number: '0.2-2') : '-'}}
                    </div>
                </td>
            </ng-container>

            <ng-container matColumnDef="olsenGrouped">
                <th mat-header-cell class="!py-3 !px-2 !text-[11px] !font-bold !text-white !bg-[var(--primary-dark)] !text-center !whitespace-nowrap !border-b !border-[var(--primary-dark)]" *matHeaderCellDef>
                    <div class="div-col">
                        <span style="font-weight: 700">Olsen</span>
                    </div>
                </th>
                <td mat-cell *matCellDef="let element" class="!py-2 !px-2 !text-[13px] !text-center !border-b !border-[var(--app-base)]">
                    <div class="analysis-element" [ngClass]="{'has-error': element?.olsen?.hasError}">
                        {{element?.olsen?.value ? (element?.olsen?.value | number: '0.2-2') : '-'}}
                    </div>
                </td>
            </ng-container>

            <ng-container matColumnDef="mehlichIii">
                <th mat-header-cell class="!py-3 !px-2 !text-[11px] !font-bold !text-white !bg-[var(--primary-dark)] !text-center !whitespace-nowrap !border-b !border-[var(--primary-dark)]" *matHeaderCellDef>
                    <div class="div-col">
                        <span style="font-weight: 700">Mehlich III</span>
                    </div>
                </th>
                <td mat-cell *matCellDef="let element" class="!py-2 !px-2 !text-[13px] !text-center !border-b !border-[var(--app-base)]">
                    <div class="analysis-element" [ngClass]="{'has-error': element?.mehlichIii?.hasError}">
                        {{element?.mehlichIii?.value ? (element?.mehlichIii?.value | number: '0.2-2') : '-'}}
                    </div>
                </td>
            </ng-container>

            <ng-container matColumnDef="citricAcid">
                <th mat-header-cell class="!py-3 !px-2 !text-[11px] !font-bold !text-white !bg-[var(--primary-dark)] !text-center !whitespace-nowrap !border-b !border-[var(--primary-dark)]" *matHeaderCellDef>
                    <div class="div-col">
                        <span style="font-weight: 700">Citric Acid</span>
                    </div>
                </th>
                <td mat-cell *matCellDef="let element" class="!py-2 !px-2 !text-[13px] !text-center !border-b !border-[var(--app-base)]">
                    <div class="analysis-element" [ngClass]="{'has-error': element?.citricAcid?.hasError}">
                        {{element?.citricAcid?.value ? (element?.citricAcid?.value | number: '0.2-2') : '-'}}
                    </div>
                </td>
            </ng-container>

            <ng-container matColumnDef="ambic">
                <th mat-header-cell *matHeaderCellDef class="!py-3 !px-2 !text-[11px] !font-bold !text-white !bg-[var(--primary-dark)] !text-center !whitespace-nowrap !border-b !border-[var(--primary-dark)]">
                    <div class="div-col">
                        <span style="font-weight: 700">Ambic</span>
                    </div>
                </th>
                <td mat-cell *matCellDef="let element" class="!py-2 !px-2 !text-[13px] !text-center !border-b !border-[var(--app-base)]">
                    <div class="analysis-element" [ngClass]="{'has-error': element?.ambic?.hasError}">
                        {{element?.ambic?.value ? (element?.ambic?.value | number: '0.2-2') : '-'}}
                    </div>
                </td>
            </ng-container>

            <tr mat-header-row *matHeaderRowDef="sampleColumns; sticky: true" style="height: auto; min-height: unset"></tr>
            <tr mat-row *matRowDef="let row; columns: sampleColumns;" class="!h-auto" style="height: auto; min-height: unset"></tr>
        </table>
    </div>
</div>

<div class="flex items-center justify-between w-full mb-2.5">
    <div></div>

    <mat-paginator style="display:none"
            (page)="onPaginateChange($event)"
            [pageSize]="pageSize"
            [pageIndex]="currentPage"
            [pageSizeOptions]="[5, 10, 20, 50, 100]"
            [showFirstLastButtons]="true"
            [length]="totalElements">
    </mat-paginator>
</div>